import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import { PermissionArea } from '@pbt/pbt-ui-components'

import type { PopperAction } from '~/components/common/ActionsPopper'
import RhapActionsPopper from '~/components/common/RhapActionsPopper'
import DialogNames from '~/constants/DialogNames'
import { useOpenInvoice } from '~/store/hooks/finance'
import { getCRUDByArea } from '~/store/reducers/auth'
import {
  getAppointmentId,
  getClientId,
  getIsFinalized,
  getPatientId,
  getSoapBusinessId,
  getSoapId,
} from '~/store/reducers/soap'
import useDialog from '~/utils/useDialog'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'

const CHARGES_WIDGET_HEADER_ESTIMATE_IMPORT = 'import-estimate'
const CHARGES_WIDGET_HEADER_ESTIMATE_CREATE = 'create-new-estimate'

type ChargesWidgetHeaderEstimateAction =
  | typeof CHARGES_WIDGET_HEADER_ESTIMATE_IMPORT
  | typeof CHARGES_WIDGET_HEADER_ESTIMATE_CREATE

const ChargesWidgetHeaderViewMenu = () => {
  const { t } = useTranslation('Payments')
  const triggerRef = useRef<HTMLButtonElement>(null)

  const clientId = useSelector(getClientId)
  const patientId = useSelector(getPatientId)
  const soapId = useSelector(getSoapId)
  const appointmentId = useSelector(getAppointmentId)
  const soapBusinessId = useSelector(getSoapBusinessId)
  const isFinalized = useSelector(getIsFinalized)
  const invoicePermissions = useSelector(getCRUDByArea(PermissionArea.INVOICE))
  const SOAPPermissions = useSelector(getCRUDByArea(PermissionArea.SOAP))

  const importEstimateDisabled =
    isFinalized || !SOAPPermissions.update || !invoicePermissions.create

  const createEstimateDisabled = isFinalized || !invoicePermissions.create

  const [openInvoiceDialog] = useDialog(DialogNames.INVOICE)
  const [openEstimatesListDialog] = useDialog(DialogNames.ESTIMATES_LIST)
  const openInvoice = useOpenInvoice(clientId, openInvoiceDialog)

  const handleSelect = (action: ChargesWidgetHeaderEstimateAction) => {
    if (action === CHARGES_WIDGET_HEADER_ESTIMATE_CREATE) {
      if (createEstimateDisabled) {
        return
      }
      openInvoice({
        clientId,
        create: true,
        eventId: appointmentId,
        invoiceId: null,
        isEstimate: true,
        newEstimateFlow: true,
        patientId,
        soapBusinessId,
        soapId,
      })
      return
    }

    if (action === CHARGES_WIDGET_HEADER_ESTIMATE_IMPORT) {
      if (importEstimateDisabled) {
        return
      }
      openEstimatesListDialog({ patientId })
    }
  }

  const actions: PopperAction[] = [
    {
      id: CHARGES_WIDGET_HEADER_ESTIMATE_IMPORT,
      label: t('CHARGES_WIDGET.IMPORT_ESTIMATE'),
      disabled: importEstimateDisabled,
      onClick: () => handleSelect(CHARGES_WIDGET_HEADER_ESTIMATE_IMPORT),
    },
    {
      id: CHARGES_WIDGET_HEADER_ESTIMATE_CREATE,
      label: t('CHARGES_WIDGET.CREATE_NEW_ESTIMATE'),
      disabled: createEstimateDisabled,
      onClick: () => handleSelect(CHARGES_WIDGET_HEADER_ESTIMATE_CREATE),
    },
  ]

  const addEstimateLabel = t('CHARGES_WIDGET.ADD_ESTIMATE')

  return (
    <div data-testid="charges-widget-header-view-menu">
      <KibButtonNew
        aria-label={addEstimateLabel}
        emphasis="tertiary"
        icon={<KibChevronDownIcon />}
        iconPosition="end"
        ref={triggerRef}
        size="small"
        theme="action"
        type="button"
      >
        {addEstimateLabel}
      </KibButtonNew>
      <RhapActionsPopper
        usePortal
        actions={actions}
        placement="bottom"
        trigger={triggerRef}
      />
    </div>
  )
}

export default ChargesWidgetHeaderViewMenu
